import React from "react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Flag, X } from "@phosphor-icons/react";

/**
 * Confirm-resign modal styled to match the rest of the Jangle UI (warm
 * parchment / deep ink, terracotta accent, Space Mono labels).
 *
 * Used by both:
 *   • The header "Exit" button — you cannot leave a live match without
 *     resigning (or finishing it normally).
 *   • The header "Resign" button — quick way to forfeit.
 */
export default function ResignConfirmModal({ open, onCancel, onConfirm, exiting = false }) {
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent
        className="sm:max-w-md bg-surface border-border p-0 overflow-hidden [&>button]:hidden"
        data-testid="resign-confirm-modal"
      >
        <DialogTitle className="sr-only">Confirm resign</DialogTitle>
        <DialogDescription className="sr-only">
          Confirm forfeiting the current match. Your opponent will be awarded the win.
        </DialogDescription>

        <div className="relative px-7 pt-7 pb-6">
          {/* Accent stripe */}
          <span aria-hidden className="absolute inset-x-0 top-0 h-1 bg-destructive/80" />

          <div className="flex items-start gap-4">
            <div className="flex-none w-12 h-12 rounded-2xl bg-destructive/15 text-destructive flex items-center justify-center">
              <Flag size={22} weight="fill" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-destructive/80">
                {exiting ? "Leaving live match" : "Confirm resign"}
              </p>
              <h2 className="font-display text-2xl font-black tracking-tight leading-tight mt-1">
                Resign this match?
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed mt-2">
                {exiting
                  ? "You can't exit a live match without resigning. Your opponent will be awarded the win and your Elo will update accordingly."
                  : "Your opponent will be awarded the win and your Elo will update accordingly."}
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-stretch gap-2 px-7 pb-7">
          <Button
            variant="outline"
            onClick={onCancel}
            className="flex-1 rounded-full border-border hover:border-foreground/30"
            data-testid="resign-cancel-btn"
          >
            <X size={15} weight="bold" className="mr-1.5" />
            {exiting ? "Keep playing" : "Cancel"}
          </Button>
          <Button
            onClick={onConfirm}
            className="flex-1 rounded-full bg-destructive hover:bg-destructive/90 text-destructive-foreground font-semibold"
            data-testid="resign-confirm-btn"
          >
            <Flag size={15} weight="fill" className="mr-1.5" />
            Resign
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
